import * as Sentry from '@sentry/react';

import useAppStore from '@duck/app-store';

const SENTRY_ACTIVE = import.meta.env.VITE_SENTRY_ACTIVE === 'true';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const APP_ENV =
  import.meta.env.VITE_ENV || import.meta.env.MODE || 'development';
const IS_LOCAL_DEV =
  APP_ENV === 'development' || APP_ENV === 'dev' || APP_ENV === 'local';

/** Drop duplicate network/5xx reports for the same endpoint within this window. */
const DEDUPE_WINDOW_MS = 60_000;
const recentTransportErrors = new Map();

function transportErrorKey(error) {
  const method = (error?.config?.method || 'get').toLowerCase();
  const url = error?.config?.url || 'unknown';
  const status = error?.response?.status || 'network';
  return `${method}:${url}:${status}`;
}

/** Returns true when this error should be sent (and marks it seen). */
function claimTransportErrorSlot(error) {
  const key = transportErrorKey(error);
  const now = Date.now();

  for (const [k, at] of recentTransportErrors) {
    if (now - at > DEDUPE_WINDOW_MS) recentTransportErrors.delete(k);
  }

  const last = recentTransportErrors.get(key);
  if (typeof last === 'number' && now - last < DEDUPE_WINDOW_MS) {
    return false;
  }

  recentTransportErrors.set(key, now);
  return true;
}

export function initSentry() {
  if (!SENTRY_ACTIVE || !SENTRY_DSN) return false;
  // Local spam guard: only init in development when explicitly forced.
  if (IS_LOCAL_DEV && import.meta.env.VITE_SENTRY_FORCE_LOCAL !== 'true') {
    return false;
  }

  const errorSampleRate = IS_LOCAL_DEV
    ? 1.0
    : Number(import.meta.env.VITE_SENTRY_SAMPLE_RATE ?? 0.5);

  // Replay buffers DOM continuously and floods envelope POSTs. Never on local —
  // FORCE_LOCAL still gets exception capture only. Deployed: sparse on-error only.
  const replaysSessionSampleRate = 0;
  const replaysOnErrorSampleRate = IS_LOCAL_DEV
    ? 0
    : Number(import.meta.env.VITE_SENTRY_REPLAY_ON_ERROR_RATE ?? 0.2);
  const replayEnabled =
    replaysSessionSampleRate > 0 ||
    (Number.isFinite(replaysOnErrorSampleRate) && replaysOnErrorSampleRate > 0);

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: APP_ENV,
    sendDefaultPii: false,
    sampleRate: Number.isFinite(errorSampleRate) ? errorSampleRate : 0.5,
    tracesSampleRate: 0,
    maxBreadcrumbs: 30,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Request aborted$/i,
      /^AbortError/i,
      /^CanceledError/i,
      /^canceled$/i,
      /Loading chunk [\d]+ failed/i,
      /Failed to fetch dynamically imported module/i,
    ],
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    integrations: replayEnabled
      ? [
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ]
      : [],
    replaysSessionSampleRate,
    replaysOnErrorSampleRate: Number.isFinite(replaysOnErrorSampleRate)
      ? replaysOnErrorSampleRate
      : 0,
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers['authorization'];
        delete event.request.headers['cookie'];
        delete event.request.headers['api-key'];
        delete event.request.headers['secure-hash'];
      }

      const values = event.exception?.values || [];
      const isCanceled = values.some((value) => {
        const type = value?.type || '';
        const valueText = value?.value || '';
        return (
          /CanceledError/i.test(type) ||
          /^canceled$/i.test(valueText) ||
          /AbortError/i.test(type)
        );
      });
      if (isCanceled) return null;

      return event;
    },
  });

  Sentry.setTag('surface', 'client');
  Sentry.setTag('app', 'training-tracker-client');

  return true;
}

export function setSentryUserFromStore() {
  if (!Sentry.getClient()) return;

  const { admin, athlete } = useAppStore.getState();
  const adminId = admin?.id || admin?.admin_id;
  const athleteId = athlete?.id || athlete?.athlete_id;

  if (adminId) {
    Sentry.setUser({
      id: String(adminId),
      username: `admin:${adminId}`,
      admin_id: String(adminId),
    });
    Sentry.setTag('surface', 'client');
    Sentry.setTag('actor', 'admin');
    return;
  }
  if (athleteId) {
    Sentry.setUser({
      id: String(athleteId),
      username: `athlete:${athleteId}`,
      athlete_id: String(athleteId),
    });
    Sentry.setTag('surface', 'client');
    Sentry.setTag('actor', 'athlete');
    return;
  }
  Sentry.setUser(null);
}

/** Report unexpected client/transport failures — not business response_code 1 messages. */
export function captureUnexpectedClientError(error, context = {}) {
  if (!Sentry.getClient()) return;

  const isAxios = Boolean(
    error?.isAxiosError || error?.response || error?.request,
  );
  const isCanceled =
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    /canceled/i.test(String(error?.message || ''));
  if (isCanceled) return;

  const status = error?.response?.status;
  const isHttpProblem = typeof status === 'number' && status >= 500;
  const isNetwork =
    isAxios && !error?.response && Boolean(error?.request || error?.message);

  if (!isHttpProblem && !isNetwork && !(error instanceof Error && !isAxios)) {
    return;
  }

  if (isAxios && !claimTransportErrorSlot(error)) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('surface', 'client');
    scope.setTag('route', window.location.pathname);
    scope.setFingerprint([
      'client-transport',
      (error?.config?.method || 'get').toLowerCase(),
      error?.config?.url || 'unknown',
      String(status || 'network'),
    ]);
    scope.setContext('location', {
      href: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
    });
    scope.setContext('browser', {
      userAgent: navigator.userAgent,
      language: navigator.language,
    });
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    if (isAxios) {
      scope.setExtra('axios_url', error.config?.url);
      scope.setExtra('axios_method', error.config?.method);
      scope.setExtra('axios_status', status);
    }
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}

export { Sentry };
